//core
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FormControl, FormHelperText, InputAdornment, IconButton} from "@mui/material";
import { isValid } from "date-fns";

//styles
import styles from './styles/styles.module.scss';
import Calendar from "@mui/icons-material/Event";

//hooks
import { useLocale } from "../../../../customHooks/useLocale";

//types
interface IMuiFieldProps {
  id?: string;
  name: string;
  autoComplete?: string;
  label: string;
  value?: any;
  secondValue?: any;
  maxDate?: any,
  minDate?: any,
  //type?: React.InputHTMLAttributes<unknown>['type'];

  hideError?: boolean,
  placeholder?: string,
  disabled?: boolean,
  className?: string,
  helperText?: string,
  dataId?: string,
  InputProps?: any,
  error?: boolean | string,
  validation?: (value: any) => any,
  validate?: (value: any) => any,
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  inputFormat?: string;
  views?: ('year' | 'month' | 'day')[];
  isRequired?: boolean;
}

export const MuiSingleDatePicker = (props: IMuiFieldProps) => {
  const [field, meta] = useField(props);
  const { t, i18n } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const {
    id,
    name,
    autoComplete,
    label,
    value,
    secondValue,
    disabled = false,
    maxDate,
    minDate,
    error = '',
    hideError = false,
    inputFormat = '',
    views = ['year', 'month', 'day'],
    isRequired = false,
  } = props;

  const [fieldVal, setFieldVal] = React.useState<Date | null>(() => {
    if (!value || value === '' || value === null || value === undefined) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
  });
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);

  const [open, setOpen] = useState(false);

  const locale = useLocale()

  // Validate date function
  const validateDate = (date: Date | null): boolean => {
    if (!date) return true; // null is considered valid (empty field)

    // Check if date is valid
    if (!isValid(date)) {
      return false;
    }

    // Check against min/max dates if provided
    if (minDate && date < new Date(minDate)) {
      return false;
    }

    if (maxDate && date > new Date(maxDate)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!value || value === '' || value === null || value === undefined) {
      setFieldVal(null);
      return;
    }
    const date = new Date(value);
    setFieldVal(isValid(date) ? date : null);
  }, [value]);

  useEffect(() => {
    if (fieldVal !== null) {
      if (name === 'at_end_show') {
        if (new Date(fieldVal) < new Date(secondValue)) {
          setFieldValue(field.name, secondValue);
          setFieldVal(secondValue);
        }
      }
      if (name === 'at_beginning_show') {
        if (new Date(fieldVal) > new Date(secondValue)) {
          setFieldValue('at_end_show', fieldVal);
          setFieldVal(fieldVal);
        }
      }
    }

  }, [fieldVal, minDate]);

  const handleDateChange = (newValue: Date | null) => {
    setFieldVal(newValue);

    const isValidDate = validateDate(newValue);
    setHasValidationError(!isValidDate);

    // Only call setFieldValue if the date is valid
    if (isValidDate) {
      setFieldValue(field.name, newValue);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale.locale}>
      <div className={styles.formField}>
        <FormControl fullWidth>
          <DatePicker
            open={open}
            onOpen={() => setOpen(true)}
            onClose={handleClose}
            label={`${label}${isRequired ? '*' : ''}`}
            disabled={disabled}
            value={fieldVal}
            format={!!inputFormat ? inputFormat : i18n.language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            views={views}
            {...(maxDate ? {maxDate: new Date(maxDate)} : {})}
            {...(minDate ? {minDate: new Date(minDate)} : {})}
            onChange={handleDateChange}
            slotProps={{
              popper: {
                disablePortal: true
              },
              textField: {
                size: 'small',
                id: id,
                className: `${styles.input} ${ 
                  !!(meta.touched && !!meta.error && meta.error !== 'error') || hasValidationError 
                    ? styles.inputError 
                    : styles.inputErrorDef 
                }`,
                ...(hideError ? {error: false} : (meta.touched && !!meta.error && meta.error !== 'error') || hasValidationError ? { error: true } : {error: false})
              },
              actionBar: ({ wrapperVariant }) => {
                return {
                  actions: wrapperVariant === 'desktop' ? [] : ['accept'],
                }
              },
            }}
          />
          { (meta.touched && !!meta.error && meta.error !== 'error') || hasValidationError ? (
            <FormHelperText error>
              {hasValidationError ? t("common.validation.invalid_date") : meta.error}
            </FormHelperText>
          ) : null }
        </FormControl>
      </div>
    </LocalizationProvider>
  );
};
